import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, isSameDay, isValid, parse } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";

const DISPLAY_FORMAT = "dd/MM/yyyy";
const TYPED_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/**
 * Parse a hand-typed `dd/MM/yyyy` (leading zeros optional). Returns undefined for
 * anything that isn't a complete, real calendar date — a partial string like "20/08"
 * must not silently resolve against today's date.
 */
function parseTypedDate(text: string): Date | undefined {
    const match = TYPED_DATE_PATTERN.exec(text.trim());
    if (!match) return undefined;

    const [, day, month, year] = match;
    const padded = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
    const parsed = parse(padded, DISPLAY_FORMAT, new Date());

    // Round-trip guard: rejects out-of-range days that a lenient parse would roll
    // over into the next month (e.g. 31/02/2021).
    if (!isValid(parsed) || format(parsed, DISPLAY_FORMAT) !== padded) return undefined;
    return parsed;
}

interface JoinDateInputProps {
    value?: Date;
    /** Fired only on a committed, valid, actually-different date. */
    onChange: (value: Date) => void;
    id?: string;
    className?: string;
    disabled?: boolean;
}

/**
 * Date field that accepts typing (`20/08/2021`) with the calendar as a secondary
 * affordance. Picking a join date years back is a handful of keystrokes instead of
 * one month-arrow click per month.
 */
export function JoinDateInput({ value, onChange, id, className, disabled }: JoinDateInputProps) {
    const [text, setText] = useState(() => (value ? format(value, DISPLAY_FORMAT) : ""));
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    // Resync when the date changes from outside — a refetch after a successful save,
    // or the edit dialog being reopened for a different staff member.
    useEffect(() => {
        setText(value ? format(value, DISPLAY_FORMAT) : "");
    }, [value]);

    const commit = (candidate: Date | undefined) => {
        if (!candidate) {
            // Unparseable or cleared: restore the last known good value rather than
            // pushing an empty join date the backend has no contract for.
            setText(value ? format(value, DISPLAY_FORMAT) : "");
            return;
        }
        setText(format(candidate, DISPLAY_FORMAT));
        if (!value || !isSameDay(candidate, value)) onChange(candidate);
    };

    return (
        <div className={cn("relative", className)}>
            <Input
                id={id}
                value={text}
                disabled={disabled}
                placeholder={DISPLAY_FORMAT.toLowerCase()}
                inputMode="numeric"
                autoComplete="off"
                className="pr-9"
                onChange={(e) => setText(e.target.value)}
                onBlur={() => commit(parseTypedDate(text))}
                onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    commit(parseTypedDate(text));
                }}
            />
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={disabled}
                        className="absolute right-0 top-0 h-full w-9 text-muted-foreground hover:bg-transparent"
                    >
                        <CalendarIcon className="h-4 w-4" />
                        <span className="sr-only">Open calendar</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={value}
                        defaultMonth={value}
                        onSelect={(date) => {
                            setIsCalendarOpen(false);
                            commit(date);
                        }}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}
